import { Info, TrendingUp, Wallet, X } from 'lucide-react';
import type { IncomeExpenseBreakdownGroup, IncomeExpenseBreakdownSide } from '../../types/dashboard';

interface IncomeExpenseDetailsPanelProps {
  /** Which side of the comparison is being explained. */
  mode: 'income' | 'expense';
  /** The account- and source-level components of that side. */
  side: IncomeExpenseBreakdownSide;
  /** Human-readable window, e.g. "Last 12 months · Nov 2025 – Oct 2026". */
  periodLabel?: string;
  /** Collapse the panel. */
  onClose: () => void;
}

function formatCurrency(value: number): string {
  return `৳${Math.round(value).toLocaleString()}`;
}

/**
 * Inline "Show details" panel for the Income vs Expenses summary tiles.
 *
 * Explains where a headline figure (total income or total expense) comes from —
 * each income/expense account with its share of the total, followed by the
 * operational source documents that posted the entries. It expands inside the
 * card instead of opening a modal, so the figures and their explanation stay
 * visible together.
 */
export function IncomeExpenseDetailsPanel({
  mode,
  side,
  periodLabel,
  onClose,
}: IncomeExpenseDetailsPanelProps) {
  const isIncome = mode === 'income';
  const accent = isIncome
    ? { chip: 'bg-success/10 text-success', text: 'text-success', bar: 'bg-success' }
    : { chip: 'bg-warning/10 text-warning', text: 'text-warning', bar: 'bg-warning' };
  const Icon = isIncome ? TrendingUp : Wallet;
  const total = side.total;
  const accountRows = side.byAccount;
  const sourceRows = side.bySource;

  const percent = (amount: number) =>
    total !== 0 ? Math.min(100, Math.abs((amount / total) * 100)) : 0;

  const renderRows = (rows: IncomeExpenseBreakdownGroup[]) => (
    <ul className="space-y-3">
      {rows.map((group) => (
        <li key={group.key}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-text">{group.label}</span>
            <span className="shrink-0 text-sm font-semibold text-text">
              {formatCurrency(group.amount)}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
              <div
                className={`h-full rounded-full ${accent.bar}`}
                style={{ width: `${percent(group.amount)}%` }}
              />
            </div>
            <span className="w-24 shrink-0 text-right text-[0.6875rem] text-text-muted">
              {total !== 0 ? `${Math.abs((group.amount / total) * 100).toFixed(1)}% · ` : ''}
              {group.entries} {group.entries === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="mt-4 rounded-xl border border-border bg-background p-4">
      <div className="flex items-start justify-between gap-3 border-b border-border pb-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`shrink-0 rounded-lg p-2 ${accent.chip}`}>
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-text">
              {isIncome ? 'Where the income comes from' : 'Where the expenses go'}
            </h3>
            {periodLabel && <p className="mt-0.5 text-xs text-text-muted">{periodLabel}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface hover:text-text"
        >
          <X size={16} />
        </button>
      </div>

      <div className="pt-3">
        {accountRows.length === 0 ? (
          <p className="py-4 text-center text-sm text-text-muted">
            No {isIncome ? 'income' : 'expense'} recorded for this period.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <section>
              <h4 className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
                By {isIncome ? 'income account' : 'expense account'}
              </h4>
              {renderRows(accountRows)}
            </section>

            {sourceRows.length > 0 && (
              <section className="border-t border-border pt-4 md:border-l md:border-t-0 md:pl-5 md:pt-0">
                <h4 className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
                  By source document
                </h4>
                {renderRows(sourceRows)}
              </section>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <span className="text-xs font-medium text-text-muted">
          Total {isIncome ? 'income' : 'expenses'}
        </span>
        <span className={`text-base font-bold ${accent.text}`}>{formatCurrency(total)}</span>
      </div>

      <p className="mt-2 flex items-start gap-1.5 text-[0.6875rem] text-text-muted">
        <Info size={13} className="mt-0.5 shrink-0" />
        Consolidated from the same ledgers as the Billing Top Sheet, so every module is counted
        once. Referral pay is a cost of income; only its unpaid portion is a payable.
      </p>
    </div>
  );
}
